import React from 'react';
import { Table, Spinner, Alert, Badge, Form, InputGroup, Button, Pagination, Row, Col } from 'react-bootstrap';
import PaginationComponent from '../common/Pagination';
import { TiTick } from 'react-icons/ti';
import useCheckAuthorization from "../../hooks/checkAuthorization";
import toast from "react-hot-toast";
import { CRUD_PERMISSIONS } from "../../constants/user";
import { IoInformationCircleSharp } from "react-icons/io5";
const IRMList = ({
  irmPeriod,
  irmList,
  loading,
  error,
  page,
  perPage,
  totalCount,
  totalPages,
  searchTerm,
  statusFilter,
  fccFilter,
  availableFccs,
  onPageChange,
  onSearchChange,
  onSearch,
  onFccFilterChange,
  onStatusFilterChange,
  summary,
  onApprove, // Add this
  onReject, // Add this
  loadingAction, // Add this
  onClearFilters
}) => {
  const approvePermission = useCheckAuthorization(
    CRUD_PERMISSIONS.ebrc.approve
  );
  const statusOptions = [
    { value: null, label: 'All Status', color: 'secondary' },
    { value: 0, label: 'Fetched', color: 'info' },
    { value: 1, label: 'Partially Matched', color: 'warning' },
    { value: 2, label: 'Fully Matched', color: 'primary' },
    { value: 3, label: 'EBRC Generated', color: 'success' },
    { value: 4, label: 'Approved', color: 'success' },
    { value: 5, label: 'Rejected', color: 'danger' }
  ];


  const getStatusBadge = (status) => {
    const statusConfig = statusOptions.find(opt => opt.value === status);
    return statusConfig ? (
      <Badge bg={statusConfig.color}>{statusConfig.label}</Badge>
    ) : (
      <Badge bg="secondary">Unknown</Badge>
    );
  };


  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount, currency = 'USD') => {
    if (!amount) return '0.00';
    return `${parseFloat(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })} ${currency}`;
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      onSearch();
    }
  };

  const handlePagination = (newPage) => {
    onPageChange(newPage);
  }

  const getIRMActionButtons = (irm) => {
    // Status 4: Approved (Show approved badge)
    if (irm.approved === 1) {
      return (
        <div className="px-3 py-2 rounded-pill fw-bold text-white"
          style={{ backgroundColor: "#28a745" }}>
          Approved
        </div>
      );
    }

    // Status 5: Rejected (Show rejected badge)
    if (irm.rejected === 1) {
      return (
        <div className="px-3 py-2 rounded-pill fw-bold text-white"
          style={{ backgroundColor: "#dc3545" }}>
          Rejected
        </div>
      );
    }

    // Status 0: EBRC Generated - Awaiting Approval
    if (irm.status === 1 && irm.approved === 0 && irm.rejected === 0) {
      // Users WITHOUT approve permission: Show waiting message
      if (!approvePermission) {
        return (
          <div className="px-3 py-2 rounded-pill fw-bold text-yellow-700"
            style={{ backgroundColor: "#fff3cd" }}>
            Waiting for Approval
          </div>
        );
      }

      // Users WITH approve permission: Show Approve/Reject buttons
      return (
        <>
          <Button
            variant="success"
            className="fw-bold px-4 shadow-sm d-flex align-items-center gap-1"
            onClick={() => onApprove(irm.id)}
            disabled={loadingAction}
          >
            <TiTick />
            Approve
          </Button>
          <Button
            variant="danger"
            className="fw-bold px-4 shadow-sm d-flex align-items-center gap-1 ms-2"
            onClick={() => onReject(irm.id)}
            disabled={loadingAction}
          >
            <TiTick />
            Reject
          </Button>
        </>
      );
    }

    // For other statuses (1, 2), show nothing or status badge only
    return null;
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-2">Loading IRM List...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <p>{error}</p>
      </Alert>
    );
  }

  return (
    <div>
      {/* Filters and Search */}
      <Row className="mb-3 align-items-end">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Search IRM</Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Search by IRM number, firm name, IEC, PAN..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <Button variant="primary" onClick={onSearch}>
                Search
              </Button>
            </InputGroup>
          </Form.Group>
        </Col>

        {/* ADD THIS - FCC Filter */}
        <Col md={3}>
          <Form.Group>
            <Form.Label>Filter by Currency</Form.Label>
            <Form.Select
              value={fccFilter}
              onChange={(e) => onFccFilterChange(e.target.value)}
            >
              <option value="">All Currencies</option>
              {availableFccs.map((fcc) => (
                <option key={fcc} value={fcc}>
                  {fcc}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        {/* ADD THIS - Clear Filters Button */}
        <Col md={2}>
          <Button
            variant="outline-secondary"
            onClick={onClearFilters}
            className="w-100"
          >
            Clear Filters
          </Button>
        </Col>
      </Row>

      {/* Table */}
      <div className="table-responsive" style={{ overflowX: 'auto' }}>
        <Table className="core-ui-table seperate-border" style={{ minWidth: '1200px' }}>
          <thead className="table-light">
            <tr>
              <th className="text-center" style={{ whiteSpace: 'nowrap' }}>SL.No</th>
              <th className="text-left" style={{ whiteSpace: 'nowrap' }}>IRM Number</th>
              <th className="text-left" style={{ whiteSpace: 'nowrap' }}>Issue Date</th>
              <th className="text-left" style={{ whiteSpace: 'nowrap' }}>IEC Number</th>
              <th className="text-left" style={{ whiteSpace: 'nowrap' }}>Firm Name</th>
              <th className="text-left" style={{ whiteSpace: 'nowrap' }}>Purpose Code</th>
              <th className="text-left" style={{ whiteSpace: 'nowrap' }}>Remittance Amount</th>
              <th className="text-left" style={{ whiteSpace: 'nowrap' }}>Matched Amount</th>
              <th className="text-left" style={{ whiteSpace: 'nowrap' }}>Balance</th>
              <th className="text-left" style={{ whiteSpace: 'nowrap' }}>AD Code</th>
              <th className="text-left" style={{ whiteSpace: 'nowrap' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {irmList.length === 0 ? (
              <tr>
                <td colSpan="11" className="text-center text-muted py-4">
                  No IRM records found
                </td>
              </tr>
            ) : (
              irmList.map((irm, index) => (
                <tr key={irm.id}>
                  <td className="text-center" style={{ whiteSpace: 'nowrap' }}>{(page - 1) * perPage + index + 1}</td>
                  <td className="text-left" style={{ whiteSpace: 'nowrap' }}>
                    <strong>{irm.irm_number}</strong>
                  </td>
                  <td className="text-left" style={{ whiteSpace: 'nowrap' }}>{formatDate(irm.irm_issue_date)}</td>
                  <td className="text-left" style={{ whiteSpace: 'nowrap' }}>
                    {irm.iec_number}
                    <br />
                    <small className="text-muted">PAN: {irm.pan}</small>
                  </td>
                  <td className="text-left" style={{ whiteSpace: 'nowrap' }}>
                    {irm.firm_name}
                    <br />
                    <small className="text-muted">{irm.remittance_name}</small>
                  </td>
                  <td className="text-left" style={{ whiteSpace: 'nowrap' }}>
                    {irm.purpose_code}
                  </td>
                  <td className="text-left" style={{ whiteSpace: 'nowrap' }}>
                    <strong>{formatCurrency(irm.remittance_amount, irm.fcc)}</strong>
                    <br />
                    <small className="text-muted">
                      Remittance: {formatDate(irm.remittance_date)}
                    </small>
                  </td>
                  <td className="text-left text-success" style={{ whiteSpace: 'nowrap' }}>
                    {formatCurrency(irm.amount_matched_with_ebrc, irm.fcc)}
                  </td>
                  <td className="text-left text-warning" style={{ whiteSpace: 'nowrap' }}>
                    <strong>{formatCurrency(irm.balance_remaining, irm.fcc)}</strong>
                  </td>
                  <td className="text-left" style={{ whiteSpace: 'nowrap' }}>
                    {irm.remittance_ad_code}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{getStatusBadge(irm.status)}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Action Buttons Section - ADD THIS NEW SECTION */}
      <div className="d-flex justify-content-between align-items-center mt-4 gap-2">
        {irmPeriod && irmPeriod.approved === 0 && irmPeriod.rejected === 0 && (
          <div className="d-flex align-items-center justify-content-center gap-2 text-muted">
            <IoInformationCircleSharp />
            <p className="mb-0">
              Approve the IRM list to unlock the Knock-Off tab.
            </p>
          </div>
        )}
        <div className="d-flex gap-2">
          {getIRMActionButtons(irmPeriod)}
        </div>
      </div>


      {/* Pagination */}
      {totalPages > 1 && (
        <Row className="mt-3">
          <Col md={6}>
            <p className="text-muted">
              Showing {(page - 1) * perPage + 1}   of {totalCount} records
            </p>
          </Col>

          <div className="row mt-10">
            <div className="col-md-12 d-flex justify-content-center">
              <PaginationComponent
                total_count={totalCount}
                current_page={page}
                per_page={perPage}
                handleOnClick={handlePagination}
              />
            </div>
          </div>
        </Row>
      )}
    </div>
  );
};

export default IRMList;